/**
 * AIS domain helpers (spec 16) — deliberately no CPA/TCPA or any derived
 * collision-avoidance math here (recorded no-alarms decision,
 * plans/README.md): this layer is situational awareness only.
 */
import { haversineNm, type LatLon } from './geo.js';

export type AisShipTypeCategory =
  | 'pleasure'
  | 'fishing'
  | 'cargo'
  | 'tanker'
  | 'passenger'
  | 'highspeed'
  | 'tug'
  | 'other';

/**
 * Maps the raw AIS "ship and cargo type" code (ITU-R M.1371/IMO, 0–99) to one
 * of ~8 broad categories the UI labels in nb-NO. Deliberately coarse — a
 * vessel-type hint for a tapped marker's sheet, not a precise classification.
 */
export function aisShipTypeCategory(shipType: number | null): AisShipTypeCategory {
  if (shipType == null || !Number.isFinite(shipType)) return 'other';
  if (shipType === 30) return 'fishing';
  if (shipType === 31 || shipType === 32 || shipType === 52) return 'tug';
  if (shipType === 36 || shipType === 37) return 'pleasure';
  if (shipType >= 40 && shipType <= 49) return 'highspeed';
  if (shipType >= 60 && shipType <= 69) return 'passenger';
  if (shipType >= 70 && shipType <= 79) return 'cargo';
  if (shipType >= 80 && shipType <= 89) return 'tanker';
  return 'other';
}

/** Effective heading for rotating the vessel marker/icon (spec 16): true
 *  heading when reported, else fall back to course over ground, else 0 (bow
 *  up — AIS gives no better hint). */
export function aisEffectiveHeading(headingDeg: number | null, cogDeg: number | null): number {
  return headingDeg ?? cogDeg ?? 0;
}

/** A target is "stale" (dimmed) past 3 min and dropped past 10 min (spec 16). */
export const AIS_DIM_AGE_MS = 3 * 60 * 1000;
export const AIS_DROP_AGE_MS = 10 * 60 * 1000;

/** Old-GPU render cap (spec 16). */
export const AIS_RENDER_CAP = 150;

export interface AisCapResult<T> {
  shown: T[];
  capped: boolean;
}

/**
 * Caps a vessel list to `cap` entries, keeping the nearest to the view
 * center first (spec 16: "212 fartøy — zoom inn" chip when capped). Pure and
 * DOM-free so `MapController.setAisVessels` can call it directly and the
 * choice of "nearest" is unit-testable without Leaflet.
 */
export function capAisVesselsByDistance<T extends LatLon>(
  vessels: T[],
  center: LatLon,
  cap: number = AIS_RENDER_CAP,
): AisCapResult<T> {
  if (vessels.length <= cap) return { shown: vessels, capped: false };
  const sorted = [...vessels].sort((a, b) => haversineNm(center, a) - haversineNm(center, b));
  return { shown: sorted.slice(0, cap), capped: true };
}
